import { useCallback } from 'react';

import { useConfluenceAnalyticsEvents } from '@confluence/analytics/entry-points/useConfluenceAnalyticsEvents.ts';
import { useDialogs } from '@confluence/dialogs/entry-points/useDialogs.ts';

import { PagePdfExportModal } from '../../entry-points/PagePdfExportModal.ts';
import type { PagePdfExportCreateDecision } from '../pagePdfExportCreateResult.ts';

import { useCreatePagePdfExportTask } from './useCreatePagePdfExportTask.ts';

type PagePdfExportSource = 'menu' | 'print-shortcut';

export type StartPagePdfExportOptions = {
	readonly contentId: string;
	/** Content type being exported (e.g. `page`, `blogpost`); used for the ARI and the modal title. */
	readonly contentType?: string;
	/**
	 * Legacy hidden-action export URL. When the backend selects a legacy (V1/V2) path, startExport
	 * navigates here. Omit it when the caller owns the legacy fallback itself (e.g. the Cmd/Ctrl+P
	 * print shortcut, which redirects with its own showPdfBanner URL) — startExport then returns the
	 * `legacy` decision without navigating.
	 */
	readonly url?: string;
	/** Which surface triggered the export; drives the Cmd/Ctrl+P info panel in the modal. */
	readonly source?: PagePdfExportSource;
};

export type UseStartPagePdfExportResult = {
	/**
	 * Runs the shared page PDF export flow and returns the create decision (so callers can fire their
	 * own analytics). Either navigates to the legacy hidden-action URL (V1/V2) or opens the Export
	 * modal (V3 / error) wired with a real "Try again" that re-runs create.
	 *
	 * Callers own eligibility (experiment gate and `preventDefault`) before calling this — it always
	 * runs as if the export flow is enabled.
	 */
	readonly startExport: (
		options: StartPagePdfExportOptions,
	) => Promise<PagePdfExportCreateDecision>;
	/** Whether a create mutation is currently in flight. */
	readonly loading: boolean;
};

/**
 * Central entry point for the page PDF export UI flow, shared by every surface that opens the Export
 * modal. Owning the create → route → open-modal decision and the `onRetry` closure here keeps retry behaviour identical
 * across surfaces instead of each one re-implementing it.
 */
export const useStartPagePdfExport = (): UseStartPagePdfExportResult => {
	const { createAndResolve, loading } = useCreatePagePdfExportTask();
	const { showModal } = useDialogs();
	const { createEvent } = useConfluenceAnalyticsEvents();

	const startExport = useCallback(
		async ({
			contentId,
			contentType,
			url,
			source = 'menu',
		}: StartPagePdfExportOptions): Promise<PagePdfExportCreateDecision> => {
			const commonAttributes = {
				contentId,
				contentType: contentType ?? null,
				exportSource: source,
			};

			// One track event per create attempt (funnel entry for every surface, including legacy which
			// opens no modal) carrying the resolved decision and backend error code. Shared by the
			// initial run and "Try again" so both are instrumented identically.
			const runCreate = async (): Promise<PagePdfExportCreateDecision> => {
				const result = await createAndResolve(contentId, contentType);

				if (result.type === 'error') {
					createEvent({
						type: 'sendTrackEvent',
						data: {
							source: 'pagePdfExport',
							action: 'failed',
							actionSubject: 'pagePdfExportCreate',
							attributes: {
								...commonAttributes,
								decision: 'error',
								reason: result.reason,
								errorCode: result.errorCode ?? null,
								statusCode: result.statusCode ?? null,
							},
						},
					}).fire();
				} else {
					createEvent({
						type: 'sendTrackEvent',
						data: {
							source: 'pagePdfExport',
							action: 'succeeded',
							actionSubject: 'pagePdfExportCreate',
							attributes: {
								...commonAttributes,
								decision: result.type,
								exportTaskId: result.type === 'modal' ? (result.exportTaskId ?? null) : null,
							},
						},
					}).fire();
				}

				return result;
			};

			// Real "Try again": re-run the same create flow. Defined once here so the overflow menu and
			// the Cmd/Ctrl+P shortcut share identical retry behaviour. A legacy decision navigates to
			// the hidden-action URL as a side effect; the modal maps modal/error decisions itself.
			const onRetry = async (): Promise<PagePdfExportCreateDecision> => {
				const retryDecision = await runCreate();
				if (retryDecision.type === 'legacy' && url) {
					window.location.assign(url);
				}
				return retryDecision;
			};

			const decision = await runCreate();

			switch (decision.type) {
				case 'legacy':
					// V1/V2: navigate to the hidden-action URL when the caller provided one. Callers that
					// own their own legacy fallback (e.g. the print shortcut) omit `url`, and act on the
					// returned `legacy` decision instead.
					if (url) {
						window.location.assign(url);
					}
					break;
				case 'modal':
					showModal(PagePdfExportModal, {
						contentId,
						contentType,
						exportTaskId: decision.exportTaskId,
						source,
						onRetry,
					});
					break;
				case 'error':
				default:
					// Open the modal in its built-in error state (null task id); "Try again" re-runs create.
					showModal(PagePdfExportModal, {
						contentId,
						contentType,
						exportTaskId: null,
						source,
						onRetry,
					});
					break;
			}

			return decision;
		},
		[createAndResolve, createEvent, showModal],
	);

	return { startExport, loading };
};
